import React from 'react';
import { useUserInfoQuery } from '@apis/query/user';
import { UserType } from '@asset/enum/UserEnum';
import { useGroupChannel } from '@sendbird/uikit-react/GroupChannel/context';
import { Message } from '@sendbird/uikit-react/GroupChannel/components/Message';
import MessageContent, { MessageBody, MessageProfile } from '@sendbird/uikit-react/ui/MessageContent';
import * as S from '@styles/chat/chat.style';
import MessageEmojiMenu from '@sendbird/uikit-react/ui/MessageItemReactionMenu';
import EmojiReactions from '@sendbird/uikit-react/ui/EmojiReactions';
import { ChatMessageType } from '@asset/enum/chatEnum';
import CustomMessageMenuButton from './CustomMessageMenuButton';
import { Button } from '@components/amc/Button';
import qs from 'qs';
import { getManageStudentStudyContentUrlObject } from 'routes/studyContent';
import { getUsernameFromNickname } from '@asset/function/chat';
import { useChatImageMessageStore, useChatStore } from '@store/chat';
import Image from 'next/image';
import AutoHeightImage from '@components/common/AutoHeightImage';
import _ from 'lodash';
import { useSendbirdStateContext } from '@sendbird/uikit-react';
import { getQuestionDetailUrlObject } from 'routes/question';
import { MediaContentMediaType } from '@apis/common/response';
import { DateTime } from 'luxon';
import { ProblemStatusModalType } from '@asset/enum/studyContentEnum';
import { getManageTotalStudyStatusListPageUrlObject } from 'routes/manageDashboard';
import GoogleMeetIcon from '@asset/svg/GoogleMeetIcon';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const CustomMessage = ({
  messageProps,
  studentInfo,
  channelInputRef
}) => {
  const {
    data: user
  } = useUserInfoQuery();
  const {
    state: {
      currentChannel
    }
  } = useGroupChannel();
  const {
    config
  } = useSendbirdStateContext();
  const unreadMessageCount = currentChannel.getUnreadMemberCount(messageProps.message);
  const readMembers = currentChannel.getReadMembers(messageProps.message);
  const isStudentRead = readMembers.some(member => member.nickname.startsWith('[S]'));
  const senderSendbirdNickname = 'sender' in messageProps.message ? messageProps.message.sender.nickname : null;
  const senderUserType = senderSendbirdNickname?.startsWith('[T]') ? UserType.TEACHER : UserType.STUDENT;
  const senderName = user && senderSendbirdNickname ? user.type === UserType.TEACHER ? getUsernameFromNickname(senderSendbirdNickname) : studentInfo?.teacherName + ' 선생님' : '';
  const hasReactions = messageProps.message.reactions.length > 0;
  const isReplyMessage = !_.isNil(messageProps.message.parentMessage);
  const isMessageByMe = 'sender' in messageProps.message ? messageProps.message.sender.userId === config.userId : false;
  const counterpartToReply = (() => {
    if (!isReplyMessage) return '누군가';
    const parentMessage = messageProps.message.parentMessage;
    if (parentMessage.isUserMessage() || parentMessage.isFileMessage() || parentMessage.isMultipleFilesMessage()) {
      if (user?.type === UserType.STUDENT) {
        return parentMessage.sender.nickname.startsWith('[S]') ? '나' : '선생님';
      }
      return getUsernameFromNickname(parentMessage.sender.nickname);
    }
    return '누군가';
  })();
  const parentMessagePhotoThumbnail = messageProps.message.parentMessage ? messageProps.message.parentMessage.isFileMessage() ? messageProps.message.parentMessage.plainUrl : null : null;
  return /*#__PURE__*/_jsx(S.ChatMessage, {
    senderUserType: senderUserType,
    myUserType: user.type,
    unreadMessageCount: unreadMessageCount,
    hasReactions: hasReactions,
    counterpartToReply: counterpartToReply,
    parentMessagePhotoThumbnail: parentMessagePhotoThumbnail,
    messageType: messageProps.message.isFileMessage() ? 'photo' : messageProps.message.isMultipleFilesMessage() ? 'multiple-photo' : 'basic',
    checkIconColor: user?.type === UserType.TEACHER && isStudentRead ? 'var(--blue-50)' : '',
    children: /*#__PURE__*/_jsx(Message, {
      ...messageProps,
      renderMessageContent: props => {
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [isReplyMessage && isMessageByMe === false && /*#__PURE__*/_jsx(S.ChatSender, {
            children: senderName
          }), /*#__PURE__*/_jsx(MessageContent, {
            ...props,
            renderSenderProfile: props => /*#__PURE__*/_jsx(S.ChatMessageProfile, {
              children: /*#__PURE__*/_jsx(MessageProfile, {
                ...props
              })
            }),
            renderMessageMenu: menuProps => /*#__PURE__*/_jsx(CustomMessageMenuButton, {
              menuProps: menuProps,
              channelInputRef: channelInputRef
            }),
            renderEmojiMenu: props => /*#__PURE__*/_jsx(S.ChatMessageEmojiMenuButton, {
              children: /*#__PURE__*/_jsx(MessageEmojiMenu, {
                ...props
              })
            }),
            renderMessageHeader: () => /*#__PURE__*/_jsx(S.ChatSender, {
              children: senderName
            }),
            renderMessageBody: props => {
              const messageCustomType = messageProps.message.customType;
              return /*#__PURE__*/_jsx(_Fragment, {
                children: messageCustomType === ChatMessageType.GOOGLE_MEET ? /*#__PURE__*/_jsx(GoogleMeetMessage, {
                  messageProps: messageProps
                }) : messageCustomType === ChatMessageType.QUESTION ? /*#__PURE__*/_jsx(QuestionMessage, {
                  messageProps: messageProps
                }) : messageProps.message.isFileMessage() || messageProps.message.isMultipleFilesMessage() ? /*#__PURE__*/_jsx(PhotoMessage, {
                  messageProps: messageProps
                }) : /*#__PURE__*/_jsx(MessageBody, {
                  ...props
                })
              });
            },
            renderEmojiReactions: props => /*#__PURE__*/_jsx(S.ChatMessageEmojiReactionContainer, {
              userType: user.type,
              children: /*#__PURE__*/_jsx(EmojiReactions, {
                ...props
              })
            })
          })]
        });
      }
    })
  });
};
export default CustomMessage;
const PhotoMessage = ({
  messageProps
}) => {
  const {
    setImages,
    setImageIndex,
    setMessageMetaInfo
  } = useChatImageMessageStore();
  const CHAT_MESSAGE_IMAGE_KEY = 'CHAT_MESSAGE_IMAGE';
  const imageURLs = messageProps.message.isFileMessage() ? [messageProps.message.url] : messageProps.message.isMultipleFilesMessage() ? messageProps.message.fileInfoList.map(fileInfo => fileInfo.url) : [];
  const fileCount = imageURLs.length;
  const senderSendbirdNickname = 'sender' in messageProps.message ? messageProps.message.sender.nickname : null;
  const senderNickname = getUsernameFromNickname(senderSendbirdNickname);
  const senderIsTeacher = senderSendbirdNickname ? senderSendbirdNickname.startsWith('[T]') : false;
  const setUpImageMessageStoreData = index => {
    setImageIndex(index);
    setImages(imageURLs.map(item => ({
      key: CHAT_MESSAGE_IMAGE_KEY,
      type: MediaContentMediaType.IMAGE,
      url: item
    })));
    setMessageMetaInfo({
      sender: senderIsTeacher ? senderNickname + ' 선생님' : senderNickname,
      sendDate: DateTime.fromMillis(messageProps.message.createdAt)
    });
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: imageURLs.length === 1 ? /*#__PURE__*/_jsx(S.ChatPhotoMessage, {
      onPointerUp: () => setUpImageMessageStoreData(0),
      children: /*#__PURE__*/_jsx(AutoHeightImage, {
        src: imageURLs[0],
        priority: true
      })
    }) : /*#__PURE__*/_jsx(S.ChatMultiplePhotoMessage, {
      multipleFilesGridCount: fileCount === 1 ? 1 : fileCount === 2 || fileCount === 4 ? 2 : 3,
      children: imageURLs.map((imageURL, index) => /*#__PURE__*/_jsx("div", {
        className: "image-wrapper",
        onPointerUp: () => setUpImageMessageStoreData(index),
        children: /*#__PURE__*/_jsx(Image, {
          alt: `채팅 이미지 ${index + 1}`,
          src: imageURL,
          sizes: "(min-width: 756px) 756px, 100vw",
          fill: true,
          style: {
            objectFit: 'cover'
          }
        })
      }, imageURL))
    })
  });
};
const GoogleMeetMessage = ({
  messageProps
}) => {
  return /*#__PURE__*/_jsxs(S.ChatGoogleMeetMessageBody, {
    onPointerUp: () => {
      window.open(messageProps.message.message, '_blank');
    },
    children: [/*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("div", {
        children: "\uD654\uC0C1 \uD68C\uC758"
      }), /*#__PURE__*/_jsx("div", {
        children: "Google Meet"
      })]
    }), /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(GoogleMeetIcon, {}), "\uCC38\uC5EC\uD558\uAE30"]
    })]
  });
};
const QuestionMessage = ({
  messageProps
}) => {
  const {
    data: user
  } = useUserInfoQuery();
  const {
    setQuestionDetailUrl
  } = useChatStore();
  const questionProblem = JSON.parse(messageProps.message.data);
  const questionProblemInfoText = `${questionProblem.materialCourse} | ${questionProblem.materialType} | ${questionProblem.materialName} | ${questionProblem.indexNameType} ${questionProblem.indexNumber} ${questionProblem.division} ${questionProblem.subIndexNumber}번`;
  return /*#__PURE__*/_jsxs("div", {
    className: "flex flex-col w-full gap-4 p-3 bg-my-gray-95 rounded-xl",
    children: [/*#__PURE__*/_jsxs("div", {
      className: "flex flex-col gap-2",
      children: [/*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx("div", {
          className: `text-title-sm-700 text-my-gray-10`,
          children: "\uC0C8\uB85C\uC6B4 \uC9C8\uBB38\uC774 \uC788\uC2B5\uB2C8\uB2E4."
        }), /*#__PURE__*/_jsx("div", {})]
      }), /*#__PURE__*/_jsx("div", {
        className: "text-body-md-500 text-my-gray-10",
        children: questionProblemInfoText
      })]
    }), /*#__PURE__*/_jsx(Button, {
      variant: "Outline",
      size: "Large",
      text: '문제 보기',
      className: "w-full",
      onPointerUp: () => {
        let pathname = null;
        let query = null;
        if (user.type === UserType.TEACHER) {
          if (questionProblem.version === 2) {
            // version 2에는 학생의 답안 상세 페이지로 이동
            const {
              pathname: _pathname,
              query: _query
            } = getManageStudentStudyContentUrlObject(questionProblem.studyContentType, questionProblem.studyContentId, {
              studentId: questionProblem.studentId,
              modalType: ProblemStatusModalType.STUDENT_ANSWER,
              pageId: questionProblem.pageId,
              problemStatusId: questionProblem.problemStatusId,
              problemStatusDetailId: questionProblem.problemStatusDetailId
            });
            pathname = _pathname;
            query = _query;
          } else if (questionProblem.version === 1 || _.isNil(questionProblem.version)) {
            // version 1에는 전체 활동 목록 페이지로 이동
            const {
              pathname: _pathname,
              query: _query
            } = getManageTotalStudyStatusListPageUrlObject({
              anchorProblemStatusDetailId: questionProblem.problemStatusDetailId,
              problemStatusDetailId: questionProblem.problemStatusDetailId
            });
            pathname = _pathname;
            query = _query;
          }
          if (pathname && query) {
            window.open(`${pathname}?${qs.stringify(query, {
              encodeValuesOnly: true,
              arrayFormat: 'comma',
              skipNulls: true
            })}`, '_blank');
          }
        } else {
          if (questionProblem.version === 2 || questionProblem.version === 1 || _.isNil(questionProblem.version)) {
            // version 1,2 모두 질문 상세 페이지로 이동
            const {
              pathname,
              query
            } = getQuestionDetailUrlObject({
              reportId: questionProblem.reportId,
              problemStatusDetailId: questionProblem.problemStatusDetailId
            });
            setQuestionDetailUrl(`${pathname}?${qs.stringify(query, {
              encodeValuesOnly: true,
              arrayFormat: 'comma',
              skipNulls: true
            })}`);
          }
        }
      }
    })]
  });
};